/**
 * Focus Trap Composable
 *
 * Provides focus management for modal dialogs to ensure keyboard accessibility.
 */
export function useFocusTrap() {
  let previouslyFocused: HTMLElement | null = null;
  let container: HTMLElement | null = null;

  const FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'a[href]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || !container) return;

    const focusableElements =
      container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  function activate(containerEl: HTMLElement | null): void {
    if (!containerEl) return;

    container = containerEl;
    previouslyFocused = document.activeElement as HTMLElement;

    const focusableElements =
      container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusableElements.length > 0) {
      setTimeout(() => {
        focusableElements[0].focus();
      }, 0);
    }

    container.addEventListener('keydown', handleKeyDown);
  }

  function deactivate(): void {
    if (container) {
      container.removeEventListener('keydown', handleKeyDown);
      container = null;
    }

    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus();
    }
    previouslyFocused = null;
  }

  return { activate, deactivate };
}
